import type { ReactNode } from 'react';
import type { PageExperience } from '@/features/experiences/types';
import { Alert, Button, Flex, Heading, Text } from '@/ui';
import { GradientCrown } from '@/ui/Icon/GradientCrown';
import { useFeatureGate, type PaywallFeature } from '../../gates';

/**
 * `link` — inline underlined text, for sitting beside a locked control.
 * `button` — a standalone secondary button.
 * `alert` — a titled callout for a transcript or empty state.
 */
type Variant = 'link' | 'button' | 'alert';

type Props = {
  feature: PaywallFeature;
  variant?: Variant;
  /** CTA text. Defaults to "Upgrade to Plus". */
  label?: string;
  /** Body copy, `alert` variant only. */
  children?: ReactNode;
  /** Heading, `alert` variant only. Defaults to "Upgrade time!". */
  title?: string;
  experience?: PageExperience;
  /** Extra query params to carry through checkout. See `useFeatureGate`. */
  redirectParams?: Record<string, string | number>;
};

/**
 * The one upgrade call-to-action. It owns nothing about *whether* a feature is
 * gated — callers decide that with `useFeatureGate` and render this when they want
 * to offer the upgrade. What it does own is the click: the analytics event, the
 * launch key that resumes the interrupted task after checkout, and which modal to
 * return to, all of which come from the feature's gate definition rather than from
 * props. That's deliberate — hardcoding `returnTo` at the call site is what made
 * the colors gate reopen the share modal it was never launched from.
 */
export const UpgradeCta = ({
  feature,
  variant = 'link',
  label = 'Upgrade to Plus',
  children,
  title = 'Upgrade time!',
  experience,
  redirectParams,
}: Props) => {
  const { promptUpgrade } = useFeatureGate(feature, {
    experience,
    redirectParams,
  });

  if (variant === 'button') {
    return (
      <Button variant="secondary" onClick={promptUpgrade}>
        <GradientCrown mr="1.5" />
        {label}
      </Button>
    );
  }

  if (variant === 'alert') {
    return (
      <Alert
        alignItems="flex-start"
        flexDirection="column"
        gap="2"
        layerStyle="magicBackground"
      >
        <Heading alignItems="baseline" as="h4" display="flex" size="xs">
          <GradientCrown mr="1.5" />
          <Text as="span">{title}</Text>
        </Heading>
        {children && <Text>{children}</Text>}
        <Button
          colorScheme="dark"
          size="sm"
          variant="solid"
          onClick={promptUpgrade}
        >
          {label}
        </Button>
      </Alert>
    );
  }

  return (
    <Flex
      align="center"
      color="gray.600"
      fontSize="sm"
      justify="center"
      lineHeight="normal"
    >
      <GradientCrown mr="1.5" />
      <Button
        _hover={{ color: 'purple.600' }}
        color="currentColor"
        display="inline"
        fontWeight="bold"
        mr="1"
        textDecor="underline"
        variant="link"
        onClick={promptUpgrade}
      >
        {label}
      </Button>
    </Flex>
  );
};
